import * as React from 'react';

import { isSSR } from 'bundles/common/utils/isSSR';
import CourseraToolbarErrorBoundary from 'bundles/coursera-toolbar/components/CourseraToolbarErrorBoundary';
import { isCourserian, isLocalDev } from 'bundles/coursera-toolbar/utils/environmentUtils';
import EpicClient from 'bundles/epic/clients/siteMisc';

// Lazy load the CourseraToolbar component and plugins
const CourseraToolbar = React.lazy(() =>
  Promise.all([
    import('bundles/coursera-toolbar/components/CourseraToolbar'),
    import('bundles/coursera-toolbar/plugins/PluginRegistry'),
    import('bundles/coursera-toolbar/plugins/epic'),
    import('bundles/coursera-toolbar/plugins/rendering'),
    import('bundles/coursera-toolbar/plugins/asg'),
    import('bundles/coursera-toolbar/plugins/previews'),
    import('bundles/coursera-toolbar/plugins/environment-switcher'),
  ])
    .then(
      ([
        toolbarModule,
        registryModule,
        epicModule,
        asgModule,
        renderingModule,
        previewsModule,
        environmentSwitcherModule,
      ]) => {
        // Explicitly register the epic plugin to ensure it's loaded
        const registry = registryModule.default;
        const epicPlugin = epicModule.default;
        const asgPlugin = asgModule.default;
        const renderingPlugin = renderingModule.default;
        const previewsPlugin = previewsModule.default;
        const environmentSwitcherPlugin = environmentSwitcherModule.default;

        registry.register(epicPlugin);
        registry.register(asgPlugin);
        registry.register(renderingPlugin);
        registry.register(previewsPlugin);
        registry.register(environmentSwitcherPlugin);

        return {
          default: toolbarModule.default,
        };
      }
    )
    .catch((error) => {
      throw error;
    })
);

// Lazy load the SSR Fallback banner
const SSRFallbackBanner = React.lazy(() => import('bundles/coursera-toolbar/components/SSRFallbackBanner'));

/**
 * Lazy wrapper for CourseraToolbar that only loads for Coursera company members or in local dev
 * This ensures zero bundle impact for non-company users in production
 */

const LazyCourseraToolbar = () => {
  const epicEnabled = EpicClient.get('enableCourseraToolbar');

  // Render the SSR fallback if we're in SSR render mode
  const showSSRFallback = isSSR() && !isLocalDev();

  if (!isCourserian() && !isLocalDev()) {
    return null;
  }

  if (!epicEnabled) {
    return null;
  }

  if (showSSRFallback) {
    return (
      <CourseraToolbarErrorBoundary>
        <React.Suspense fallback={null}>
          <SSRFallbackBanner />
        </React.Suspense>
      </CourseraToolbarErrorBoundary>
    );
  }

  return (
    <CourseraToolbarErrorBoundary>
      <React.Suspense fallback={null}>
        <CourseraToolbar />
      </React.Suspense>
    </CourseraToolbarErrorBoundary>
  );
};

export default LazyCourseraToolbar;
